import { useQuery } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { format } from '@teetsh/app/src/utils/time/format';
import { APPELS_KEY } from '@teetsh/app/src/queries/appels/useAppels';
import deserializeDateFromServer from '@teetsh/app/src/utils/time/deserializeDateFromServer';
import parseISO from '@teetsh/app/src/utils/time/parseISO';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';

interface Props {
  from: Date;
  to: Date;
  eleveId?: string;
}

export function getAppelsAbsencesBetweenDates({
  schoolyearId,
  schoolId,
  from,
  to,
  tz,
  eleveId,
}: {
  schoolyearId: string;
  schoolId: string;
  from: Date;
  to: Date;
  tz: string;
  eleveId?: string;
}) {
  let requestURL = `${
    import.meta.env.VITE_API_URL
  }/schoolyears/${schoolyearId}/schools/${schoolId}/appels/eleves-absences?from=${format(
    from,
    'fr-FR',
  )}&to=${format(to, 'fr-FR')}`;
  if (eleveId) {
    requestURL += `&eleve_id=${eleveId}`;
  }
  return request(requestURL, {
    method: 'GET',
  }).then<ElevesAbsences>(data => ({
    eleves: data.eleves.map(elv => ({
      ...elv,
      absences: elv.absences.map(abs => ({
        ...abs,
        date: deserializeDateFromServer(parseISO(abs.date), tz),
      })),
    })),
  }));
}

export interface Absence {
  date: Date;
  absenceType: string;
  type: string;
}

export interface EleveAbsences {
  eleveId: string;
  givenName: string;
  familyName: string;
  absences: Absence[];
}

export interface ElevesAbsences {
  eleves: EleveAbsences[];
}

export function useAppelAbsences(props: Props) {
  const { from, to, eleveId } = props;
  const user = useUser();
  const { schoolyear, school } = useSchoolSchoolyear();
  return useQuery({
    queryKey: [
      schoolyear.id,
      school.id,
      APPELS_KEY,
      'absences',
      format(from, 'fr-FR'),
      format(to, 'fr-FR'),
      eleveId,
    ],
    queryFn: () =>
      getAppelsAbsencesBetweenDates({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        from,
        to,
        tz: user.location,
        eleveId,
      }),

    placeholderData: { eleves: [] },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}
